import type { ArenaRun, ArenaRunStatus, CandidateResult } from './hooks/useModelArena';
import { dayLabel, modelLabel } from './runLabels';

const TERMINAL: ArenaRunStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED'];

/** A run still to happen or still happening. These are the ones worth watching, and the only ones
 * that can still be stopped. */
export const isInFlight = (run: ArenaRun) => !TERMINAL.includes(run.status);

/** How much of a comparison is done. Candidates are saved as each finishes, so this is measured
 * rather than guessed — but it moves a whole candidate at a time, which is tens of minutes. */
export const progress = (run: ArenaRun) => ({
  measured: run.results.length,
  total: run.candidates.length,
});

/** When a run happened, or when it is due to — a queued run has not started yet. */
export const runMoment = (run: ArenaRun): string => run.startedAt ?? run.notBefore;

/** The candidate leading on one metric. A headline, not a ranking — the leaderboard sorts itself. */
export const topBy = (
  results: CandidateResult[],
  metric: string | null,
): CandidateResult | undefined =>
  // Nothing to order by leaves the order they ran in, which is the honest default for a task that
  // has no single number meaning "better".
  metric === null
    ? results[0]
    : results.reduce<CandidateResult | undefined>((best, result) => {
        const value = Number(result.metrics[metric] ?? Number.NEGATIVE_INFINITY);
        const incumbent = best
          ? Number(best.metrics[metric] ?? Number.NEGATIVE_INFINITY)
          : Number.NEGATIVE_INFINITY;
        return best === undefined || value > incumbent ? result : best;
      }, undefined);

/** Matches a run against free text: its models, status, the metric values it scored and the day it
 * ran, so "failed" and "today" are searches as much as a model name is. */
export const matchesQuery = (run: ArenaRun, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const haystack = [
    run.status,
    run.fixtures.name,
    run.fixtures.version,
    run.triggeredBy ?? '',
    dayLabel(runMoment(run)),
    ...run.candidates,
    ...run.results.flatMap(result => [
      modelLabel(result),
      ...Object.entries(result.metrics).map(([name, value]) => `${name} ${value}`),
    ]),
  ]
    .join(' ')
    .toLowerCase();

  return needle
    .split(' ')
    .filter(Boolean)
    .every(token => haystack.includes(token));
};

type RunsByDay = { day: string; runs: ArenaRun[] }[];

/** Runs newest first, grouped under the day they ran, preserving that order. */
export const groupByDay = (runs: ArenaRun[]): RunsByDay => {
  const ordered = [...runs].sort(
    (a, b) => new Date(runMoment(b)).getTime() - new Date(runMoment(a)).getTime(),
  );

  const days: RunsByDay = [];
  for (const run of ordered) {
    const day = dayLabel(runMoment(run));
    const current = days.at(-1);
    if (current?.day === day) {
      current.runs.push(run);
    } else {
      days.push({ day, runs: [run] });
    }
  }
  return days;
};
